// Banking Center — QuickBooks-style unified bank-feed workspace.
//
// Replaces the split "Connected Accounts" + "Transactions" screens with a
// single page that matches QBO's Banking dashboard:
//   - Top: horizontal bank tiles showing each account with balance,
//     "In QuickBooks"/"For review" split, last sync, and a connect button
//   - Middle: tabs For Review / Categorized / Excluded
//   - Bottom: transaction grid with inline categorize, split, exclude,
//     bulk actions, and a GL account picker per row
//
// Everything here reuses existing API routes under /api/finance/stripe/*
// plus the new /api/finance/stripe/transactions/bulk-categorize endpoint.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box, Paper, Stack, Typography, Button, Chip, IconButton, Tooltip,
    Tabs, Tab, Checkbox, TextField, Autocomplete, MenuItem, Select,
    FormControl, InputLabel, TableContainer, Table, TableHead, TableBody,
    TableRow, TableCell, TablePagination, LinearProgress, Alert,
    Card, CardContent, Skeleton, Divider, CircularProgress,
    Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SyncIcon from '@mui/icons-material/Sync';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import BlockIcon from '@mui/icons-material/Block';
import UndoIcon from '@mui/icons-material/Undo';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HistoryIcon from '@mui/icons-material/History';
import SavingsIcon from '@mui/icons-material/Savings';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import RemoveIcon from '@mui/icons-material/Remove';
import ScheduleIcon from '@mui/icons-material/Schedule';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import { useSnackbar } from 'notistack';
import axiosInstance from '../../../utils/axiosInstance';
import StripeLinkButton from '../components/StripeLinkButton';
import ManualUploadDialog from './ManualUploadDialog';
import FileUploadIcon from '@mui/icons-material/FileUpload';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const TYPE_ICONS = {
    DEPOSITORY: <SavingsIcon />,
    CREDIT: <CreditCardIcon />,
    LOAN: <ReceiptLongIcon />,
    INVESTMENT: <TrendingUpIcon />,
    OTHER: <AccountBalanceWalletIcon />,
};

const TYPE_COLORS = {
    DEPOSITORY: '#22c55e',
    CREDIT: '#f59e0b',
    LOAN: '#ef4444',
    INVESTMENT: '#8b5cf6',
    OTHER: '#64748b',
};

const fmtMoney = (v, cur = 'USD') => {
    if (v == null) return '—';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: cur || 'USD' }).format(Number(v));
};

const fmtDate = (s) => {
    if (!s) return '—';
    return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const fmtRelTime = (s) => {
    if (!s) return 'never';
    const d = new Date(s);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
};

// Tab id → query params for list endpoint. Tabs 0-2 use the transactions
// grid; tabs 3-4 render dedicated panels (balance history / connections).
const TAB_FILTERS = {
    0: { is_categorized: 'false', status: 'ACTIVE' }, // For Review
    1: { is_categorized: 'true', status: 'ACTIVE' },  // Categorized
    2: { status: 'REMOVED' },                           // Excluded
};
const TAB_LABELS = ['For Review', 'Categorized', 'Excluded', 'Balance History'];
const TXN_TABS = new Set([0, 1, 2]);


// -------- Bank tile --------

function BankTile({ account, selected, onClick, onSync, onSchedule, onDisconnect, syncing }) {
    const color = TYPE_COLORS[account.type] || '#64748b';
    const icon = TYPE_ICONS[account.type] || TYPE_ICONS.OTHER;
    return (
        <Paper
            sx={{
                p: 2, minWidth: 240, cursor: 'pointer',
                borderLeft: `4px solid ${color}`,
                borderTop: selected ? `2px solid ${color}` : '2px solid transparent',
                borderRight: selected ? `2px solid ${color}` : '2px solid transparent',
                borderBottom: selected ? `2px solid ${color}` : '2px solid transparent',
                bgcolor: selected ? `${color}0A` : '#fff',
                transition: 'all 0.15s',
                '&:hover': { bgcolor: `${color}0A`, transform: 'translateY(-1px)' },
            }}
            onClick={onClick}
        >
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
                <Box sx={{ color }}>{icon}</Box>
                <Typography variant="caption" sx={{ fontFamily, fontWeight: 600, color: '#64748b' }}>
                    {account.institution_name || 'Bank'}
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Tooltip title="Sync this account">
                    <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); onSync(account); }}
                        disabled={syncing}
                    >
                        <SyncIcon sx={{ fontSize: 16, animation: syncing ? 'spin 1s linear infinite' : 'none' }} />
                    </IconButton>
                </Tooltip>
                {onSchedule && (
                    <Tooltip title="Sync schedule">
                        <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); onSchedule(account); }}
                        >
                            <ScheduleIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                )}
                {onDisconnect && (
                    <Tooltip title="Disconnect">
                        <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); onDisconnect(account); }}
                            sx={{ color: '#cbd5e1', '&:hover': { color: '#ef4444' } }}
                        >
                            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                )}
            </Stack>
            <Typography variant="body2" sx={{ fontFamily, fontWeight: 600, color: '#1e293b' }}>
                {account.name || account.official_name || 'Account'}
                {account.mask && <span style={{ color: '#64748b', marginLeft: 4 }}>•••{account.mask}</span>}
            </Typography>
            <Typography variant="h6" sx={{ fontFamily, fontWeight: 700, mt: 0.5 }}>
                {fmtMoney(account.current_balance, account.iso_currency_code)}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                {account.is_manual && (
                    <Chip size="small" label="Manual" variant="outlined"
                          sx={{ fontFamily, fontSize: 10, height: 20, borderColor: '#94a3b8', color: '#475569' }} />
                )}
                {account.for_review_count > 0 && (
                    <Chip size="small" color="warning"
                          label={`${account.for_review_count} to review`}
                          sx={{ fontFamily, fontSize: 11, height: 20 }} />
                )}
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily }}>
                    {account.is_manual ? 'CSV import' : `synced ${fmtRelTime(account.updated_at)}`}
                </Typography>
            </Stack>
        </Paper>
    );
}

// -------- Per-row categorize picker --------

function CategorizePicker({ txn, glAccounts, onChange, disabled }) {
    const current = glAccounts.find(a => a.id === txn.gl_account_id) || null;
    return (
        <Autocomplete
            size="small"
            options={glAccounts}
            value={current}
            getOptionLabel={(o) => `${o.account_number} — ${o.name}`}
            renderOption={(props, opt) => (
                <li {...props} key={opt.id}>
                    <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                        <Typography variant="caption" sx={{ fontFamily, fontWeight: 600, color: '#64748b', minWidth: 50 }}>
                            {opt.account_number}
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily, flexGrow: 1 }}>{opt.name}</Typography>
                        <Chip label={opt.account_type} size="small" sx={{ fontSize: 10, height: 18 }} />
                    </Box>
                </li>
            )}
            sx={{ minWidth: 240 }}
            renderInput={(p) => <TextField {...p} placeholder="Pick a category…" />}
            onChange={(_, val) => onChange(val ? val.id : null)}
            disabled={disabled}
            disableClearable={false}
        />
    );
}

// -------- Main page --------

export default function BankingCenter() {
    const { enqueueSnackbar } = useSnackbar();

    // Bank accounts
    const [accounts, setAccounts] = useState([]);
    const [accountsLoading, setAccountsLoading] = useState(true);
    const [selectedAccount, setSelectedAccount] = useState(null); // null = all
    const [syncingId, setSyncingId] = useState(null);
    const [syncingAll, setSyncingAll] = useState(false);

    // Transactions
    const [tab, setTab] = useState(0);
    const [txns, setTxns] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(25);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [glAccounts, setGlAccounts] = useState([]);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [rowSaving, setRowSaving] = useState({});

    // Balance History tab
    const [snapshots, setSnapshots] = useState([]);
    const [snapshotsLoading, setSnapshotsLoading] = useState(false);
    const [snapshotting, setSnapshotting] = useState(false);
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    // Connections tab
    const [disconnectDialog, setDisconnectDialog] = useState({ open: false, itemId: null, name: '' });
    const [disconnecting, setDisconnecting] = useState(false);

    // Linked JEs (bank_txn_id -> {entry_number, journal_entry_id, entry_date})
    const [linkedJes, setLinkedJes] = useState({});

    // Manual CSV upload dialog
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);

    // Sync schedule dialog
    const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
    const [scheduleConnectionId, setScheduleConnectionId] = useState(null);
    const [scheduleData, setScheduleData] = useState({
        enabled: true,
        frequency: 'EVERY_6_HOURS',
        time_of_day: '09:00',
        day_of_week: 1,
    });
    const [scheduleLoading, setScheduleLoading] = useState(false);
    const [scheduleSaving, setScheduleSaving] = useState(false);
    const [scheduleInfo, setScheduleInfo] = useState(null); // last_run_at, next_run_at, status

    const fetchAccounts = useCallback(async () => {
        setAccountsLoading(true);
        try {
            const res = await axiosInstance.get('/api/finance/stripe/accounts');
            if (res.data?.success) {
                const list = res.data.data || [];
                setAccounts(list);
                // Auto-select the first account since there's no longer an
                // "All Accounts" tile to show a combined view.
                setSelectedAccount((prev) => {
                    if (!list.length) return null;
                    if (!prev) return list[0];
                    return list.find((a) => a.id === prev.id) || list[0];
                });
            }
        } catch (e) {
            setError(e.response?.data?.message || e.message);
        } finally {
            setAccountsLoading(false);
        }
    }, []);

    const fetchGlAccounts = useCallback(async () => {
        try {
            const res = await axiosInstance.get('/api/finance/coa', {
                params: { flat: true, status: 'ACTIVE' },
            });
            if (res.data?.success) {
                setGlAccounts((res.data.data || []).filter(a => !a.is_header));
            }
        } catch { /* ignore */ }
    }, []);

    const fetchTxns = useCallback(async () => {
        if (!TXN_TABS.has(tab)) return;
        setLoading(true);
        try {
            const params = {
                page: page + 1,
                per_page: perPage,
                ...TAB_FILTERS[tab],
                ...(selectedAccount ? { stripe_account_id: selectedAccount.id } : {}),
                ...(search ? { search } : {}),
                ...(fromDate ? { from_date: fromDate } : {}),
                ...(toDate ? { to_date: toDate } : {}),
            };
            const res = await axiosInstance.get('/api/finance/stripe/transactions', { params });
            let rows = [];
            if (res.data?.success) {
                rows = res.data.data || [];
                setTxns(rows);
                setTotal(res.data.total || 0);
            }
            setSelectedIds(new Set());

            // Fetch linked JEs for the Categorized tab so the UI can show
            // "JE#N" click-through chips. We only care about categorized rows.
            const needsJe = rows.filter(r => r.is_categorized).map(r => r.id);
            if (needsJe.length > 0) {
                try {
                    const jeRes = await axiosInstance.post(
                        '/api/finance/stripe/transactions/linked-jes',
                        { ids: needsJe },
                    );
                    setLinkedJes(jeRes.data?.data || {});
                } catch { /* best-effort */ }
            } else {
                setLinkedJes({});
            }
        } catch (e) {
            setError(e.response?.data?.message || e.message);
        } finally {
            setLoading(false);
        }
    }, [page, perPage, tab, selectedAccount, search, fromDate, toDate]);

    const fetchSnapshots = useCallback(async () => {
        if (tab !== 3) return;
        // Balance History requires a specific account — pick the first tile if none selected
        const acctId = selectedAccount?.id || accounts[0]?.id;
        if (!acctId) { setSnapshots([]); return; }
        setSnapshotsLoading(true);
        try {
            const params = {};
            if (dateFrom) params.from_date = dateFrom;
            if (dateTo) params.to_date = dateTo;
            const res = await axiosInstance.get(
                `/api/finance/stripe/balance-snapshots/${acctId}`, { params });
            if (res.data?.success) setSnapshots(res.data.data || []);
        } catch (e) {
            enqueueSnackbar(e.response?.data?.message || 'Failed to load balance history', { variant: 'error' });
        } finally {
            setSnapshotsLoading(false);
        }
    }, [tab, selectedAccount, accounts, dateFrom, dateTo, enqueueSnackbar]);

    const takeSnapshot = async () => {
        setSnapshotting(true);
        try {
            await axiosInstance.post('/api/finance/stripe/balance-snapshots/take-all', {});
            enqueueSnackbar('Balance snapshots captured', { variant: 'success' });
            fetchSnapshots();
            fetchAccounts();
        } catch (e) {
            enqueueSnackbar(e.response?.data?.message || 'Failed', { variant: 'error' });
        } finally {
            setSnapshotting(false);
        }
    };

    const openScheduleDialog = async (connectionId) => {
        if (!connectionId) {
            enqueueSnackbar('No connection linked to this account', { variant: 'warning' });
            return;
        }
        setScheduleConnectionId(connectionId);
        setScheduleDialogOpen(true);
        setScheduleLoading(true);
        setScheduleInfo(null);
        try {
            const res = await axiosInstance.get(
                `/api/finance/stripe/connections/${connectionId}/sync-schedule`);
            const s = res.data?.data?.schedule;
            if (s) {
                setScheduleData({
                    enabled: !!s.enabled,
                    frequency: s.frequency || 'EVERY_6_HOURS',
                    time_of_day: s.time_of_day || '09:00',
                    day_of_week: s.day_of_week ?? 1,
                });
                setScheduleInfo({
                    next_run_at: s.next_run_at,
                    last_run_at: s.last_run_at,
                    last_run_status: s.last_run_status,
                    last_run_added: s.last_run_added,
                    last_run_message: s.last_run_message,
                });
            } else {
                // No schedule yet — defaults already set
                setScheduleData({
                    enabled: true,
                    frequency: 'EVERY_6_HOURS',
                    time_of_day: '09:00',
                    day_of_week: 1,
                });
            }
        } catch (e) {
            enqueueSnackbar(e.response?.data?.message || 'Failed to load schedule', { variant: 'error' });
        } finally {
            setScheduleLoading(false);
        }
    };

    const saveSchedule = async () => {
        if (!scheduleConnectionId) return;
        setScheduleSaving(true);
        try {
            const payload = {
                enabled: scheduleData.enabled,
                frequency: scheduleData.frequency,
                time_of_day: ['DAILY', 'WEEKLY'].includes(scheduleData.frequency) ? scheduleData.time_of_day : null,
                day_of_week: scheduleData.frequency === 'WEEKLY' ? scheduleData.day_of_week : null,
            };
            const res = await axiosInstance.put(
                `/api/finance/stripe/connections/${scheduleConnectionId}/sync-schedule`,
                payload,
            );
            enqueueSnackbar('Sync schedule saved', { variant: 'success' });
            setScheduleInfo({
                next_run_at: res.data?.data?.next_run_at,
                last_run_at: res.data?.data?.last_run_at,
                last_run_status: res.data?.data?.last_run_status,
            });
        } catch (e) {
            enqueueSnackbar(e.response?.data?.message || 'Failed to save schedule', { variant: 'error' });
        } finally {
            setScheduleSaving(false);
        }
    };

    const removeSchedule = async () => {
        if (!scheduleConnectionId) return;
        setScheduleSaving(true);
        try {
            await axiosInstance.delete(
                `/api/finance/stripe/connections/${scheduleConnectionId}/sync-schedule`);
            enqueueSnackbar('Reverted to default schedule', { variant: 'info' });
            setScheduleDialogOpen(false);
        } catch (e) {
            enqueueSnackbar(e.response?.data?.message || 'Failed', { variant: 'error' });
        } finally {
            setScheduleSaving(false);
        }
    };

    const confirmDisconnect = async () => {
        if (!disconnectDialog.itemId) return;
        setDisconnecting(true);
        try {
            await axiosInstance.delete(`/api/finance/stripe/connections/${disconnectDialog.itemId}`);
            enqueueSnackbar('Bank disconnected', { variant: 'success' });
            setDisconnectDialog({ open: false, itemId: null, name: '' });
            fetchAccounts();
        } catch (e) {
            enqueueSnackbar(e.response?.data?.message || 'Disconnect failed', { variant: 'error' });
        } finally {
            setDisconnecting(false);
        }
    };

    useEffect(() => { fetchAccounts(); fetchGlAccounts(); }, [fetchAccounts, fetchGlAccounts]);
    useEffect(() => { fetchTxns(); }, [fetchTxns]);
    useEffect(() => { fetchSnapshots(); }, [fetchSnapshots]);

    const syncOne = async (acct) => {
        const connId = acct.stripe_connection_id;
        if (!connId) return;
        setSyncingId(acct.id);
        try {
            await axiosInstance.post(`/api/finance/stripe/transactions/sync/${connId}`);
            enqueueSnackbar('Synced', { variant: 'success' });
            fetchAccounts(); fetchTxns();
        } catch (e) {
            enqueueSnackbar(e.response?.data?.message || 'Sync failed', { variant: 'error' });
        } finally {
            setSyncingId(null);
        }
    };

    const syncAll = async () => {
        setSyncingAll(true);
        try {
            await axiosInstance.post('/api/finance/stripe/transactions/sync-all');
            enqueueSnackbar('All accounts synced', { variant: 'success' });
            fetchAccounts(); fetchTxns();
        } catch (e) {
            enqueueSnackbar(e.response?.data?.message || 'Sync failed', { variant: 'error' });
        } finally {
            setSyncingAll(false);
        }
    };

    const categorizeRow = async (txnId, glAccountId) => {
        setRowSaving((s) => ({ ...s, [txnId]: true }));
        try {
            await axiosInstance.put(`/api/finance/stripe/transactions/${txnId}`, {
                gl_account_id: glAccountId,
            });
            enqueueSnackbar(glAccountId ? 'Category saved' : 'Category cleared', { variant: 'success' });
            fetchTxns();
        } catch (e) {
            enqueueSnackbar(e.response?.data?.message || 'Update failed', { variant: 'error' });
        } finally {
            setRowSaving((s) => ({ ...s, [txnId]: false }));
        }
    };

    const postRowToGl = async (txnId) => {
        setRowSaving((s) => ({ ...s, [txnId]: true }));
        try {
            const res = await axiosInstance.post(
                `/api/finance/stripe/transactions/${txnId}/post-to-gl`);
            if (res.data?.journal_entry_id) {
                enqueueSnackbar(`Posted JE ${res.data.entry_number}`, { variant: 'success' });
            } else if (res.data?.posting_warning) {
                enqueueSnackbar(res.data.posting_warning, { variant: 'warning' });
            }
            fetchTxns();
        } catch (e) {
            enqueueSnackbar(e.response?.data?.message || 'Post failed', { variant: 'error' });
        } finally {
            setRowSaving((s) => ({ ...s, [txnId]: false }));
        }
    };

    const unpostRow = async (txnId) => {
        setRowSaving((s) => ({ ...s, [txnId]: true }));
        try {
            await axiosInstance.post(`/api/finance/stripe/transactions/${txnId}/unpost`);
            enqueueSnackbar('Unposted — journal entry reversed', { variant: 'info' });
            fetchTxns();
        } catch (e) {
            enqueueSnackbar(e.response?.data?.message || 'Unpost failed', { variant: 'error' });
        } finally {
            setRowSaving((s) => ({ ...s, [txnId]: false }));
        }
    };

    const excludeRow = async (txnId) => {
        setRowSaving((s) => ({ ...s, [txnId]: true }));
        try {
            await axiosInstance.put(`/api/finance/stripe/transactions/${txnId}`, {
                action: 'exclude',
            });
            enqueueSnackbar('Excluded', { variant: 'info' });
            fetchTxns();
        } catch (e) {
            enqueueSnackbar(e.response?.data?.message || 'Exclude failed', { variant: 'error' });
        } finally {
            setRowSaving((s) => ({ ...s, [txnId]: false }));
        }
    };

    const includeRow = async (txnId) => {
        setRowSaving((s) => ({ ...s, [txnId]: true }));
        try {
            await axiosInstance.put(`/api/finance/stripe/transactions/${txnId}`, {
                action: 'include',
            });
            fetchTxns();
        } catch (e) {
            enqueueSnackbar(e.response?.data?.message || 'Restore failed', { variant: 'error' });
        } finally {
            setRowSaving((s) => ({ ...s, [txnId]: false }));
        }
    };

    const toggleSelect = (id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const toggleSelectAll = () => {
        if (selectedIds.size === txns.length && txns.length > 0) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(txns.map(t => t.id)));
        }
    };

    const [bulkAccountId, setBulkAccountId] = useState(null);

    const bulkCategorize = async () => {
        if (!bulkAccountId || selectedIds.size === 0) return;
        setLoading(true);
        try {
            const res = await axiosInstance.post('/api/finance/stripe/transactions/bulk-categorize', {
                items: [...selectedIds].map(id => ({ id, gl_account_id: bulkAccountId })),
            });
            const s = res.data?.data?.succeeded || 0;
            const f = res.data?.data?.failed || 0;
            enqueueSnackbar(`Categorized ${s}${f ? `, ${f} failed` : ''}`,
                { variant: f ? 'warning' : 'success' });
            fetchTxns();
            setBulkAccountId(null);
        } catch (e) {
            enqueueSnackbar(e.response?.data?.message || 'Bulk update failed', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const bulkPost = async () => {
        if (selectedIds.size === 0) return;
        setLoading(true);
        try {
            const res = await axiosInstance.post('/api/finance/stripe/transactions/bulk-post', {
                ids: [...selectedIds],
            });
            const s = res.data?.data?.succeeded || 0;
            const f = res.data?.data?.failed || 0;
            enqueueSnackbar(`Posted ${s} JE${s === 1 ? '' : 's'}${f ? `, ${f} failed` : ''}`,
                { variant: f ? 'warning' : 'success' });
            fetchTxns();
        } catch (e) {
            enqueueSnackbar(e.response?.data?.message || 'Bulk post failed', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    // Stats per account (for tile badge counts)
    const accountsWithStats = useMemo(() => accounts, [accounts]);

    return (
        <Box sx={{ p: 3, fontFamily }}>
            {/* Header */}
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <AccountBalanceIcon sx={{ fontSize: 32, color: ACCENT }} />
                <Typography variant="h5" sx={{ fontWeight: 700, fontFamily }}>
                    Banking
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                <StripeLinkButton onConnected={() => { fetchAccounts(); fetchTxns(); }} />
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<FileUploadIcon />}
                    onClick={() => setUploadDialogOpen(true)}
                    sx={{ fontFamily, textTransform: 'none', borderColor: ACCENT, color: ACCENT,
                          '&:hover': { borderColor: '#16a34a', bgcolor: `${ACCENT}10` } }}
                >
                    Upload CSV
                </Button>
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<SyncIcon sx={{ animation: syncingAll ? 'spin 1s linear infinite' : 'none' }} />}
                    onClick={syncAll}
                    disabled={syncingAll || accounts.length === 0}
                >
                    Sync all
                </Button>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontFamily }}>
                Review, categorize, and post bank feed transactions to your General Ledger in one place.
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            {/* Bank tiles — click a tile to filter transactions to that
                account. Each tile has inline Sync, Schedule, and Disconnect
                actions. */}
            <Stack direction="row" spacing={1.5} sx={{ overflowX: 'auto', pb: 1, mb: 2 }}>
                {accountsLoading && Array.from({ length: 3 }).map((_, i) => (
                    <Skeleton key={i} variant="rounded" width={240} height={110} />
                ))}
                {accountsWithStats.map((acct) => (
                    <BankTile
                        key={acct.id}
                        account={acct}
                        selected={selectedAccount?.id === acct.id}
                        onClick={() => setSelectedAccount(acct)}
                        onSync={syncOne}
                        onSchedule={(a) => openScheduleDialog(a.stripe_connection_id)}
                        onDisconnect={(a) => setDisconnectDialog({
                            open: true,
                            itemId: a.stripe_connection_id,
                            name: a.institution_name || a.name,
                        })}
                        syncing={syncingId === acct.id}
                    />
                ))}
                {!accountsLoading && accountsWithStats.length === 0 && (
                    <Paper sx={{ p: 2, minWidth: 280, bgcolor: '#f8fafc', border: '1px dashed #cbd5e1' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontFamily }}>
                            No banks connected yet. Connect your first account above.
                        </Typography>
                    </Paper>
                )}
            </Stack>

            {/* Tabs + search + date filter */}
            <Paper sx={{ mb: 2 }}>
                <Stack direction="row" alignItems="center" sx={{ px: 2, borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap', gap: 1 }}>
                    <Tabs value={tab} onChange={(_, v) => { setTab(v); setPage(0); }}
                          sx={{ flexGrow: 1, minHeight: 40 }}>
                        {TAB_LABELS.map((l, i) => <Tab key={i} label={l} sx={{ minHeight: 40, fontFamily, textTransform: 'none' }} />)}
                    </Tabs>
                    {/* Date range filter — applies to the current tab */}
                    {TXN_TABS.has(tab) && (
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ my: 0.5 }}>
                            <TextField
                                size="small" type="date"
                                label="From" InputLabelProps={{ shrink: true }}
                                value={fromDate}
                                onChange={(e) => { setFromDate(e.target.value); setPage(0); }}
                                sx={{ width: 150 }}
                            />
                            <TextField
                                size="small" type="date"
                                label="To" InputLabelProps={{ shrink: true }}
                                value={toDate}
                                onChange={(e) => { setToDate(e.target.value); setPage(0); }}
                                sx={{ width: 150 }}
                            />
                            {(fromDate || toDate) && (
                                <Tooltip title="Clear date filter">
                                    <IconButton size="small"
                                                onClick={() => { setFromDate(''); setToDate(''); setPage(0); }}>
                                        <BlockIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Stack>
                    )}
                    <TextField
                        size="small"
                        placeholder="Search merchant / description…"
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                        InputProps={{ startAdornment: <SearchIcon sx={{ fontSize: 18, mr: 1, color: '#94a3b8' }} /> }}
                        sx={{ width: 260, my: 0.5 }}
                    />
                </Stack>

                {/* Bulk bar (txn tabs only) */}
                {TXN_TABS.has(tab) && selectedIds.size > 0 && (
                    <Box sx={{ px: 2, py: 1, bgcolor: '#eff6ff', borderBottom: '1px solid #dbeafe',
                               display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" sx={{ fontFamily, fontWeight: 600 }}>
                            {selectedIds.size} selected
                        </Typography>
                        <Autocomplete
                            size="small"
                            options={glAccounts}
                            getOptionLabel={(o) => `${o.account_number} — ${o.name}`}
                            value={glAccounts.find(a => a.id === bulkAccountId) || null}
                            onChange={(_, val) => setBulkAccountId(val ? val.id : null)}
                            sx={{ minWidth: 280 }}
                            renderInput={(p) => <TextField {...p} placeholder="Categorize all as…" />}
                        />
                        <Button size="small" variant="contained" onClick={bulkCategorize}
                                disabled={!bulkAccountId}>
                            Apply
                        </Button>
                        {tab === 1 && (
                            <Button size="small" variant="contained" color="success"
                                    onClick={bulkPost}>
                                Post to GL
                            </Button>
                        )}
                        <Button size="small" onClick={() => setSelectedIds(new Set())}>
                            Clear
                        </Button>
                    </Box>
                )}

                {TXN_TABS.has(tab) && loading && <LinearProgress />}
                {!TXN_TABS.has(tab) && (snapshotsLoading) && <LinearProgress />}

                {TXN_TABS.has(tab) && (
                <>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        indeterminate={selectedIds.size > 0 && selectedIds.size < txns.length}
                                        checked={txns.length > 0 && selectedIds.size === txns.length}
                                        onChange={toggleSelectAll}
                                    />
                                </TableCell>
                                <TableCell>Date</TableCell>
                                <TableCell>Description</TableCell>
                                <TableCell>Account</TableCell>
                                <TableCell align="right">Amount</TableCell>
                                {tab !== 2 && <TableCell sx={{ minWidth: 280 }}>Category</TableCell>}
                                <TableCell align="center">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {txns.map((t) => (
                                <TableRow key={t.id} hover
                                          sx={{
                                              bgcolor: selectedIds.has(t.id) ? '#eff6ff' : 'inherit',
                                              opacity: t.status === 'REMOVED' ? 0.6 : 1,
                                          }}>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={selectedIds.has(t.id)}
                                            onChange={() => toggleSelect(t.id)}
                                            disabled={t.status === 'REMOVED'}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontFamily }}>
                                            {fmtDate(t.date)}
                                        </Typography>
                                        {t.is_pending && (
                                            <Chip label="Pending" size="small"
                                                  sx={{ fontSize: 10, height: 18, ml: 0 }} />
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontFamily, fontWeight: 600 }}>
                                            {t.merchant_name || t.name || '—'}
                                        </Typography>
                                        {t.category_primary && (
                                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily }}>
                                                {t.category_primary.replace(/_/g, ' ').toLowerCase()}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily }}>
                                            {t.account_name || t.account_mask || '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2" sx={{
                                            fontFamily, fontWeight: 700,
                                            color: Number(t.amount) > 0 ? '#ef4444' : '#10b981',
                                        }}>
                                            {Number(t.amount) > 0 ? '−' : '+'}{fmtMoney(Math.abs(Number(t.amount)), t.iso_currency_code)}
                                        </Typography>
                                    </TableCell>
                                    {tab !== 2 && (
                                        <TableCell>
                                            <CategorizePicker
                                                txn={t}
                                                glAccounts={glAccounts}
                                                onChange={(id) => categorizeRow(t.id, id)}
                                                disabled={rowSaving[t.id]}
                                            />
                                        </TableCell>
                                    )}
                                    <TableCell align="center">
                                        {tab === 2 ? (
                                            <Tooltip title="Restore to For Review">
                                                <IconButton size="small" onClick={() => includeRow(t.id)}
                                                            disabled={rowSaving[t.id]}>
                                                    <UndoIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        ) : (
                                            <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
                                                {t.is_categorized && linkedJes[t.id] ? (
                                                    <>
                                                        <Tooltip title={`Open journal entry ${linkedJes[t.id].entry_number}`}>
                                                            <Chip
                                                                size="small"
                                                                clickable
                                                                color="success"
                                                                icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                                                                label={linkedJes[t.id].entry_number}
                                                                onClick={() => window.open(
                                                                    `/finance-portal/journal-entries?id=${linkedJes[t.id].journal_entry_id}`,
                                                                    '_blank',
                                                                )}
                                                                sx={{ fontFamily, fontWeight: 600, height: 22 }}
                                                            />
                                                        </Tooltip>
                                                        <Tooltip title="Unpost (reverse this journal entry)">
                                                            <IconButton size="small"
                                                                        onClick={() => unpostRow(t.id)}
                                                                        disabled={rowSaving[t.id]}>
                                                                <UndoIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </>
                                                ) : t.is_categorized ? (
                                                    <Tooltip title="Post to General Ledger">
                                                        <Button size="small" variant="outlined"
                                                                color="success"
                                                                onClick={() => postRowToGl(t.id)}
                                                                disabled={rowSaving[t.id]}
                                                                sx={{
                                                                    fontFamily, textTransform: 'none',
                                                                    minWidth: 70, height: 24, fontSize: 11,
                                                                }}>
                                                            Post
                                                        </Button>
                                                    </Tooltip>
                                                ) : null}
                                                <Tooltip title="Exclude from books">
                                                    <IconButton size="small" onClick={() => excludeRow(t.id)}
                                                                disabled={rowSaving[t.id]}>
                                                        <BlockIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                            {!loading && txns.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={tab === 2 ? 6 : 7} align="center" sx={{ py: 4 }}>
                                        <HistoryIcon sx={{ fontSize: 40, color: '#cbd5e1', mb: 1 }} />
                                        <Typography variant="body2" color="text.secondary" sx={{ fontFamily }}>
                                            {tab === 0 && 'Nothing to review — everything is categorized.'}
                                            {tab === 1 && 'No categorized transactions yet.'}
                                            {tab === 2 && 'No excluded transactions.'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={total}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={perPage}
                    onRowsPerPageChange={(e) => { setPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                />
                </>
                )}

                {/* Balance History tab */}
                {tab === 3 && (
                    <Box sx={{ p: 2 }}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap' }}>
                            <Typography variant="body2" sx={{ fontFamily, fontWeight: 600 }}>
                                {selectedAccount?.name || accounts[0]?.name || 'Select an account'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily }}>
                                {selectedAccount?.mask ? `•••${selectedAccount.mask}` :
                                 accounts[0]?.mask ? `•••${accounts[0].mask}` : ''}
                            </Typography>
                            <Box sx={{ flexGrow: 1 }} />
                            <TextField size="small" type="date" label="From"
                                       InputLabelProps={{ shrink: true }}
                                       value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                                       sx={{ width: 150 }} />
                            <TextField size="small" type="date" label="To"
                                       InputLabelProps={{ shrink: true }}
                                       value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                                       sx={{ width: 150 }} />
                            <Button size="small" variant="contained"
                                    startIcon={snapshotting ? <CircularProgress size={14} color="inherit" /> : <CameraAltIcon />}
                                    onClick={takeSnapshot} disabled={snapshotting}
                                    sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' }, textTransform: 'none' }}>
                                Take snapshot
                            </Button>
                        </Stack>
                        {accounts.length === 0 ? (
                            <Alert severity="info">Connect a bank to see balance history.</Alert>
                        ) : (
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>When</TableCell>
                                        <TableCell align="right">Current balance</TableCell>
                                        <TableCell align="right">Available</TableCell>
                                        <TableCell align="right">Δ vs prior</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {snapshots.length === 0 && !snapshotsLoading && (
                                        <TableRow>
                                            <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                                                <Typography variant="body2" color="text.secondary" sx={{ fontFamily }}>
                                                    No snapshots yet. Click "Take snapshot" to start the series.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {snapshots.map((s, i) => {
                                        const prev = snapshots[i + 1];
                                        const diff = prev ? Number(s.current_balance || 0) - Number(prev.current_balance || 0) : null;
                                        return (
                                            <TableRow key={s.id}>
                                                <TableCell>
                                                    {new Date(s.captured_at || s.created_at).toLocaleString('en-US', {
                                                        month: 'short', day: 'numeric', year: 'numeric',
                                                        hour: 'numeric', minute: '2-digit'
                                                    })}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {fmtMoney(s.current_balance, s.iso_currency_code)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {fmtMoney(s.available_balance, s.iso_currency_code)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {diff == null ? (
                                                        <RemoveIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
                                                    ) : diff > 0 ? (
                                                        <Stack direction="row" spacing={0.25} alignItems="center" justifyContent="flex-end">
                                                            <ArrowUpwardIcon sx={{ fontSize: 14, color: ACCENT }} />
                                                            <Typography variant="caption" sx={{ color: ACCENT, fontFamily, fontWeight: 600 }}>
                                                                {fmtMoney(diff)}
                                                            </Typography>
                                                        </Stack>
                                                    ) : diff < 0 ? (
                                                        <Stack direction="row" spacing={0.25} alignItems="center" justifyContent="flex-end">
                                                            <ArrowDownwardIcon sx={{ fontSize: 14, color: '#ef4444' }} />
                                                            <Typography variant="caption" sx={{ color: '#ef4444', fontFamily, fontWeight: 600 }}>
                                                                {fmtMoney(Math.abs(diff))}
                                                            </Typography>
                                                        </Stack>
                                                    ) : '—'}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        )}
                    </Box>
                )}

            </Paper>

            {/* Sync schedule dialog */}
            <Dialog open={scheduleDialogOpen} onClose={() => setScheduleDialogOpen(false)}
                    maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>
                    Sync schedule
                </DialogTitle>
                <DialogContent>
                    {scheduleLoading ? (
                        <CircularProgress size={20} />
                    ) : (
                        <Stack spacing={2} sx={{ pt: 1 }}>
                            <FormControlLabel
                                control={<Switch checked={scheduleData.enabled}
                                                 onChange={(e) => setScheduleData({ ...scheduleData, enabled: e.target.checked })} />}
                                label={
                                    <Typography variant="body2" sx={{ fontFamily, fontWeight: 600 }}>
                                        {scheduleData.enabled ? 'Auto-sync enabled' : 'Auto-sync disabled (manual only)'}
                                    </Typography>
                                }
                            />
                            <FormControl size="small" fullWidth disabled={!scheduleData.enabled}>
                                <InputLabel>Frequency</InputLabel>
                                <Select
                                    label="Frequency"
                                    value={scheduleData.frequency}
                                    onChange={(e) => setScheduleData({ ...scheduleData, frequency: e.target.value })}
                                >
                                    <MenuItem value="HOURLY">Every hour</MenuItem>
                                    <MenuItem value="EVERY_6_HOURS">Every 6 hours</MenuItem>
                                    <MenuItem value="EVERY_12_HOURS">Every 12 hours</MenuItem>
                                    <MenuItem value="DAILY">Once a day</MenuItem>
                                    <MenuItem value="WEEKLY">Once a week</MenuItem>
                                    <MenuItem value="MANUAL">Manual only</MenuItem>
                                </Select>
                            </FormControl>
                            {['DAILY', 'WEEKLY'].includes(scheduleData.frequency) && scheduleData.enabled && (
                                <TextField
                                    size="small" type="time" label="Time of day (UTC)"
                                    InputLabelProps={{ shrink: true }}
                                    value={scheduleData.time_of_day}
                                    onChange={(e) => setScheduleData({ ...scheduleData, time_of_day: e.target.value })}
                                />
                            )}
                            {scheduleData.frequency === 'WEEKLY' && scheduleData.enabled && (
                                <FormControl size="small" fullWidth>
                                    <InputLabel>Day of week</InputLabel>
                                    <Select
                                        label="Day of week"
                                        value={scheduleData.day_of_week}
                                        onChange={(e) => setScheduleData({ ...scheduleData, day_of_week: Number(e.target.value) })}
                                    >
                                        <MenuItem value={0}>Monday</MenuItem>
                                        <MenuItem value={1}>Tuesday</MenuItem>
                                        <MenuItem value={2}>Wednesday</MenuItem>
                                        <MenuItem value={3}>Thursday</MenuItem>
                                        <MenuItem value={4}>Friday</MenuItem>
                                        <MenuItem value={5}>Saturday</MenuItem>
                                        <MenuItem value={6}>Sunday</MenuItem>
                                    </Select>
                                </FormControl>
                            )}
                            {scheduleInfo && (
                                <Alert severity={
                                    scheduleInfo.last_run_status === 'ERROR' ? 'error' :
                                    scheduleInfo.last_run_status === 'OK' ? 'success' : 'info'
                                }>
                                    <Typography variant="caption" sx={{ fontFamily, display: 'block' }}>
                                        Next run: {scheduleInfo.next_run_at ?
                                            new Date(scheduleInfo.next_run_at).toLocaleString() : 'not scheduled'}
                                    </Typography>
                                    {scheduleInfo.last_run_at && (
                                        <Typography variant="caption" sx={{ fontFamily, display: 'block' }}>
                                            Last run: {new Date(scheduleInfo.last_run_at).toLocaleString()}
                                            {scheduleInfo.last_run_status && ` (${scheduleInfo.last_run_status})`}
                                            {scheduleInfo.last_run_added != null && ` — added ${scheduleInfo.last_run_added} txns`}
                                        </Typography>
                                    )}
                                    {scheduleInfo.last_run_message && (
                                        <Typography variant="caption" sx={{ fontFamily, display: 'block', color: 'error.main' }}>
                                            {scheduleInfo.last_run_message}
                                        </Typography>
                                    )}
                                </Alert>
                            )}
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={removeSchedule} color="inherit"
                            disabled={scheduleSaving || scheduleLoading}>
                        Use default
                    </Button>
                    <Box sx={{ flexGrow: 1 }} />
                    <Button onClick={() => setScheduleDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={saveSchedule}
                            disabled={scheduleSaving || scheduleLoading}
                            startIcon={scheduleSaving ? <CircularProgress size={14} color="inherit" /> : null}
                            sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}>
                        Save schedule
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Manual CSV upload dialog */}
            <ManualUploadDialog
                open={uploadDialogOpen}
                onClose={() => setUploadDialogOpen(false)}
                onImported={() => { fetchAccounts(); fetchTxns(); }}
                accounts={accounts}
            />

            {/* Disconnect confirmation */}
            <Dialog open={disconnectDialog.open}
                    onClose={() => setDisconnectDialog({ open: false, itemId: null, name: '' })}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>
                    Disconnect {disconnectDialog.name}?
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ fontFamily }}>
                        The bank connection will be revoked and no new transactions will sync.
                        Existing transactions remain in the system.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDisconnectDialog({ open: false, itemId: null, name: '' })}>
                        Cancel
                    </Button>
                    <Button color="error" variant="contained" onClick={confirmDisconnect} disabled={disconnecting}
                            startIcon={disconnecting ? <CircularProgress size={14} color="inherit" /> : <DeleteOutlineIcon />}>
                        {disconnecting ? 'Disconnecting…' : 'Disconnect'}
                    </Button>
                </DialogActions>
            </Dialog>

            <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        </Box>
    );
}
